import { useState } from "react";
import type { ExperimentDetail } from "@/mocks/experimentDetail";

interface EditorPanelProps {
  detail: ExperimentDetail;
}

interface SectionProps {
  icon: string;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function Section({ icon, title, children, defaultOpen = true }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-2xl border border-[#E5E6EB] bg-white shadow-[0_1px_0_rgba(15,23,42,0.02)] overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-4 h-11 hover:bg-[#F9FAFB] transition-colors cursor-pointer"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-[#F5F6F9] text-[#646479]">
            <i className={`${icon} text-[13px]`}></i>
          </div>
          <span className="text-[12.5px] font-semibold text-[#1D2129]">{title}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); }}
            className="w-6 h-6 flex items-center justify-center rounded-lg text-[#646479] hover:text-[#3D69E8] hover:bg-[#F5F6F9] cursor-pointer"
          >
            <i className="ri-add-line text-sm"></i>
          </button>
          <div className="w-5 h-5 flex items-center justify-center text-[#646479]">
            <i className={`${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`}></i>
          </div>
        </div>
      </button>
      {open && <div className="px-3 pb-3 pt-1">{children}</div>}
    </div>
  );
}

function ItemRow({ icon, name }: { icon: string; name: string }) {
  return (
    <button className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#F9FAFB] group cursor-pointer text-left transition-colors">
      <div className="w-6 h-6 flex items-center justify-center bg-[#F2F3F5] rounded-lg flex-shrink-0">
        <i className={`${icon} text-[11px] text-[#646479]`}></i>
      </div>
      <span className="text-[12px] text-[#4E5969] flex-1 truncate">{name}</span>
      <div className="w-5 h-5 flex items-center justify-center text-[#C9CDD4] opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
        <i className="ri-more-line text-xs"></i>
      </div>
    </button>
  );
}

export default function EditorPanel({ detail }: EditorPanelProps) {
  const [openingText, setOpeningText] = useState(detail.openingStatement);

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center justify-between px-4 h-11 border-b border-[#E5E6EB] flex-shrink-0 bg-white">
        <span className="text-[13px] font-semibold text-[#1D2129]">编辑</span>
        <button className="w-7 h-7 flex items-center justify-center rounded-lg text-[#646479] hover:text-[#1D2129] hover:bg-[#F5F6F9] transition-colors cursor-pointer">
          <i className="ri-settings-3-line text-[13px]"></i>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-[#FAFAFA]">

        <Section icon="ri-cpu-line" title="模型">
          <button className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-[#F9FAFB] cursor-pointer transition-colors text-left">
            <div className="w-6 h-6 flex items-center justify-center bg-[#3D69E8]/10 rounded-lg flex-shrink-0">
              <i className="ri-sparkling-line text-[11px] text-[#3D69E8]"></i>
            </div>
            <span className="text-[12px] font-medium text-[#1D2129] flex-1 truncate">{detail.model}</span>
            <div className="w-5 h-5 flex items-center justify-center text-[#C9CDD4]">
              <i className="ri-arrow-right-s-line text-xs"></i>
            </div>
          </button>
        </Section>

        {/* 工作流 */}
        <Section icon="ri-flow-chart" title="工作流">
          {detail.workflows.map((w, i) => (
            <ItemRow key={i} icon={w.icon} name={w.name} />
          ))}
        </Section>

        {/* MCP */}
        <Section icon="ri-plug-line" title="MCP">
          {detail.mcpTools.map((m, i) => (
            <ItemRow key={i} icon={m.icon} name={m.name} />
          ))}
        </Section>

        {/* Skill */}
        <Section icon="ri-tools-line" title="Skill">
          {detail.skills.map((s, i) => (
            <ItemRow key={i} icon={s.icon} name={s.name} />
          ))}
        </Section>

        {/* 知识库 */}
        <Section icon="ri-book-2-line" title="知识库">
          {detail.knowledgeBases.map((k, i) => (
            <ItemRow key={i} icon={k.icon} name={k.name} />
          ))}
        </Section>

        {/* 数据库 */}
        <Section icon="ri-database-2-line" title="数据库">
          {detail.databases.map((d, i) => (
            <ItemRow key={i} icon={d.icon} name={d.name} />
          ))}
        </Section>

        {/* 记忆 */}
        <Section icon="ri-mental-health-line" title="记忆">
          <div className="space-y-1.5 py-1">
            {[
              { label: "记忆容量", value: String(detail.memory.capacity), unit: "条" },
              { label: "记忆保留时长", value: String(detail.memory.retentionHours), unit: "小时" },
              { label: "记忆更新频率", value: String(detail.memory.updateFrequency), unit: "次 / 小时" },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-2 px-2">
                <span className="text-[12px] text-[#646479] w-24 flex-shrink-0">{row.label}</span>
                <div className="flex-1 flex items-center h-7 border border-[#E5E6EB] rounded px-2 bg-white">
                  <input
                    defaultValue={row.value}
                    className="flex-1 text-[12px] text-[#1D2129] bg-transparent outline-none w-0"
                  />
                  <span className="text-[11px] text-[#C9CDD4] ml-1 whitespace-nowrap">{row.unit}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* 用户体验 */}
        <Section icon="ri-user-smile-line" title="用户体验">
          <div className="space-y-3">
            <div>
              <div className="text-[11px] font-medium text-[#8B8FA8] mb-1.5">开场白文案</div>
              <textarea
                value={openingText}
                onChange={(e) => setOpeningText(e.target.value)}
                rows={3}
                className="w-full text-[12px] text-[#1D2129] bg-white border border-[#E5E6EB] rounded-xl px-3 py-2.5 outline-none resize-none focus:border-[#3D69E8] focus:ring-2 focus:ring-[#3D69E8]/10 transition-all leading-[1.7]"
              />
            </div>
            <div>
              <div className="text-[11px] font-medium text-[#8B8FA8] mb-1.5">预置推荐问题</div>
              <div className="space-y-2">
                {detail.suggestedQuestions.map((q, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1 h-8 flex items-center border border-[#E5E6EB] rounded-xl px-3 bg-white">
                      <span className="text-[12px] text-[#4E5969] truncate">{q}</span>
                    </div>
                    <button className="w-8 h-8 flex items-center justify-center text-[#C9CDD4] hover:text-[#E43F1E] hover:bg-[#F9FAFB] rounded-xl cursor-pointer transition-colors">
                      <i className="ri-delete-bin-line text-xs"></i>
                    </button>
                  </div>
                ))}
              </div>
              <button className="mt-2 inline-flex items-center gap-1.5 text-[12px] text-[#3D69E8] hover:text-[#2d57d1] cursor-pointer">
                <i className="ri-add-line text-xs"></i>
                <span>添加推荐问题</span>
              </button>
            </div>
          </div>
        </Section>

      </div>
    </div>
  );
}
